import { Ban, ShieldCheck, SlidersHorizontal, UserRound, UsersRound } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Chip } from "@/components/chip";
import {
  DataTable,
  SortableHeadCell,
  TABLE_ICON_CLASS,
  TableCell,
  TableEmptyRow,
  TableFacts,
  TableHead,
  TableHeadCell,
  TableItem,
  TableRow,
} from "@/components/data-table";
import { FilterBar } from "@/components/filter-bar";
import { FilterToggle } from "@/components/filter-toggle";
import { Highlight } from "@/components/highlight";
import { RowActions } from "@/components/row-action";
import { SearchMultiSelect } from "@/components/search-multi-select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { Person, TeamsOverview } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { personLabel } from "@/lib/people";
import { sortRows, useTableSort } from "@/lib/table-sort";
import { cn } from "@/lib/utils";

import type { BlockTarget } from "./block-dialog";
import {
  emailsWithExceptions,
  filterPeople,
  hasPeopleFilters,
  NO_PEOPLE_FILTERS,
  NO_TEAM,
  PEOPLE_SORT_COLUMNS,
  type PeopleFilters,
  peopleSortValue,
} from "./people";
import {
  accessLine,
  type AppGrant,
  baseAccess,
  exceptionsOf,
  type GrantRow,
  namedTeams,
  personAccessCount,
  teamLabel,
} from "./utils";

/** Person, teams, access, last call, actions. */
const COLUMNS = 5;

interface PeopleTableProps {
  overview: TeamsOverview;
  rows: readonly GrantRow[];
  apps: readonly AppGrant[];
  /** Opens the exceptions of a person, by email. */
  onExceptions: (email: string) => void;
  /** Opens the block dialog for a person, or the unblock dialog for someone blocked. */
  onBlock: (person: BlockTarget) => void;
}

/**
 * Everyone the page knows: their name (or email), a "Geblokkeerd" chip when
 * Velta blocked their use of DC Data, their teams, what they can use in Claude
 * in one line with the apps they may open (worked out from the teams and
 * their exceptions as the server does), their last call, and their actions:
 * Uitzonderingen, and Blokkeren or Deblokkeren. Someone who never signed in
 * to DC Data has no Microsoft id to block by: their block action is disabled
 * and says why. Every column sorts (the access by how much it gives); a
 * search, the teams (with "Iedereen zonder team"), "Geblokkeerd" and "Met
 * uitzonderingen" filter. In the table's phone layout the access, the teams
 * and the last call move under the name, and the actions into one menu.
 */
export function PeopleTable({ overview, rows, apps, onExceptions, onBlock }: PeopleTableProps) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<PeopleFilters>(NO_PEOPLE_FILTERS);
  const sort = useTableSort({ columns: PEOPLE_SORT_COLUMNS, initial: null });
  const teams = namedTeams(overview);
  const teamNames = new Map(teams.map((team) => [team.id, teamLabel(team)]));
  const namesOf = (person: Person) => person.teams.flatMap((id) => teamNames.get(id) ?? []);
  const withExceptions = emailsWithExceptions(overview.overrides);
  const { matching, shown } = filterPeople(overview.people, filters, withExceptions);
  const accessOf = (person: Person) =>
    personAccessCount(
      baseAccess(person.email, teams, overview.default_team),
      exceptionsOf(person.email, overview.overrides),
      rows,
      apps,
    );
  const sorted = sortRows(shown, sort.sort, (person, column) =>
    peopleSortValue(
      person,
      column,
      namesOf(person),
      column === "access" ? accessOf(person) : undefined,
    ),
  );
  const set = (change: Partial<PeopleFilters>) => setFilters({ ...filters, ...change });
  const filtered = hasPeopleFilters(filters);
  const reset = () => setFilters(NO_PEOPLE_FILTERS);
  const teamIcon = <UsersRound strokeWidth={1.75} className="text-primary size-4" />;

  return (
    <div className="space-y-3">
      <FilterBar
        search={{
          value: filters.search,
          onChange: (search) => set({ search }),
          label: t("teams.people.search"),
        }}
        columns={["medium", "auto", "autoRow"]}
        filtered={filtered}
        onReset={reset}
      >
        <SearchMultiSelect
          options={[
            { value: NO_TEAM, label: t("teams.defaultTeam"), icon: teamIcon },
            ...teams.map((team) => ({ value: team.id, label: teamLabel(team), icon: teamIcon })),
          ]}
          value={filters.teams}
          onChange={(chosen) => set({ teams: chosen })}
          name={t("teams.people.column.teams")}
          allLabel={t("teams.people.allTeams")}
          countLabel={(count) => t("teams.people.teamCount", { count })}
          searchLabel={t("teams.people.searchTeam")}
          className="w-full"
        />
        <FilterToggle
          label={t("teams.people.blocked")}
          count={matching.filter((person) => person.blocked).length}
          pressed={filters.blocked}
          onPressedChange={(blocked) => set({ blocked })}
          className="w-full justify-between"
        />
        <FilterToggle
          label={t("teams.people.withExceptions")}
          count={matching.filter((person) => withExceptions.has(person.email.toLowerCase())).length}
          pressed={filters.withExceptions}
          onPressedChange={(withExceptionsOnly) => set({ withExceptions: withExceptionsOnly })}
          className="w-full justify-between"
        />
      </FilterBar>
      <DataTable label={t("teams.people.title")}>
        <TableHead>
          <SortableHeadCell {...sort.head("person")} className="md:min-w-52">
            {t("teams.people.column.person")}
          </SortableHeadCell>
          <SortableHeadCell {...sort.head("teams")} narrow="hide">
            {t("teams.people.column.teams")}
          </SortableHeadCell>
          <SortableHeadCell {...sort.head("access")} narrow="hide">
            {t("teams.people.column.access")}
          </SortableHeadCell>
          <SortableHeadCell {...sort.head("last")} narrow="hide">
            {t("teams.people.column.lastCall")}
          </SortableHeadCell>
          <TableHeadCell align="right" className="w-px">
            <span className="sr-only">{t("teams.column.actions")}</span>
          </TableHeadCell>
        </TableHead>
        <tbody>
          {sorted.map((person) => (
            <PersonRow
              key={person.email}
              person={person}
              teams={namesOf(person)}
              overview={overview}
              rows={rows}
              apps={apps}
              search={filters.search}
              onExceptions={onExceptions}
              onBlock={onBlock}
            />
          ))}
          {sorted.length === 0 && (
            <TableEmptyRow colSpan={COLUMNS} onReset={filtered ? reset : undefined}>
              {overview.people.length === 0 ? t("teams.people.empty") : t("teams.people.noMatch")}
            </TableEmptyRow>
          )}
        </tbody>
      </DataTable>
    </div>
  );
}

interface PersonRowProps {
  person: Person;
  /** The names of the teams they are in. */
  teams: string[];
  overview: TeamsOverview;
  rows: readonly GrantRow[];
  apps: readonly AppGrant[];
  search: string;
  onExceptions: (email: string) => void;
  onBlock: (person: BlockTarget) => void;
}

function PersonRow({
  person,
  teams,
  overview,
  rows,
  apps,
  search,
  onExceptions,
  onBlock,
}: PersonRowProps) {
  const { t } = useTranslation();
  const label = personLabel(person);
  const base = baseAccess(person.email, namedTeams(overview), overview.default_team);
  const exceptions = exceptionsOf(person.email, overview.overrides);
  const summary = accessLine(base, exceptions, rows, apps);
  const lastCall = person.last_call_at ? formatDateTime(person.last_call_at) : null;
  const teamChips = (
    <span className="flex flex-wrap gap-1">
      {teams.length === 0 ? (
        <span className="text-muted text-xs">{t("teams.defaultTeam")}</span>
      ) : (
        teams.map((name) => <Chip key={name}>{name}</Chip>)
      )}
    </span>
  );
  const target: BlockTarget = { label, userOid: person.user_oid, blocked: person.blocked };

  return (
    <TableRow className="align-top">
      <TableCell>
        <TableItem
          icon={
            <UserRound
              aria-hidden="true"
              strokeWidth={1.75}
              className={cn(TABLE_ICON_CLASS, person.blocked && "opacity-60")}
            />
          }
          title={
            <span className="break-words">
              <Highlight text={label} query={search} />
            </span>
          }
          summary={
            // "Geblokkeerd" on a line of its own under the name and the email, the same
            // place for someone known by name and someone known by email alone.
            (label !== person.email || person.blocked) && (
              <>
                {label !== person.email && (
                  <span className="block break-all">
                    <Highlight text={person.email} query={search} />
                  </span>
                )}
                {person.blocked && (
                  <span className="mt-1 block">
                    <BlockedChip person={person} />
                  </span>
                )}
              </>
            )
          }
          facts={
            // The row reads top to bottom: the access, the teams, then the last call.
            <>
              <p>{summary}</p>
              {teamChips}
              <TableFacts>
                {lastCall
                  ? t("teams.people.lastCallFact", { time: lastCall })
                  : t("teams.people.noCalls")}
              </TableFacts>
            </>
          }
          muted={person.blocked}
        />
      </TableCell>
      <TableCell narrow="hide">{teamChips}</TableCell>
      <TableCell narrow="hide" className="text-foreground text-xs">
        {summary}
      </TableCell>
      <TableCell
        narrow="hide"
        className={cn("text-xs whitespace-nowrap tabular-nums", !lastCall && "text-muted")}
      >
        {lastCall ?? t("teams.people.noCalls")}
      </TableCell>
      <TableCell align="right" className="px-1 py-1">
        <RowActions
          label={t("common.actionsFor", { name: label })}
          actions={[
            {
              label: t("teams.people.exceptionsFor", { person: label }),
              short: t("teams.people.exceptions"),
              icon: SlidersHorizontal,
              onClick: () => onExceptions(person.email),
            },
            person.blocked
              ? {
                  id: "block",
                  label: t("teams.people.unblockFor", { person: label }),
                  short: t("teams.people.unblock"),
                  icon: ShieldCheck,
                  onClick: () => onBlock(target),
                }
              : {
                  id: "block",
                  label: t("teams.people.blockFor", { person: label }),
                  short: t("teams.people.block"),
                  icon: Ban,
                  onClick: () => onBlock(target),
                  disabledHint: person.user_oid ? undefined : t("teams.people.neverSignedIn"),
                },
          ]}
        />
      </TableCell>
    </TableRow>
  );
}

/**
 * "Geblokkeerd", on its own line under the person's name and email, with when
 * and by whom in its tooltip, from a focusable chip.
 */
function BlockedChip({ person }: { person: Person }) {
  const { t } = useTranslation();
  const when = person.blocked_at ? formatDateTime(person.blocked_at) : null;
  const since =
    when && person.blocked_by_email
      ? t("teams.people.blockedAtBy", { time: when, email: person.blocked_by_email })
      : when
        ? t("teams.people.blockedAt", { time: when })
        : null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Chip
          tone="danger"
          tabIndex={0}
          className="focus-visible:ring-ring self-center font-normal focus-visible:ring-2 focus-visible:outline-none"
        >
          {t("teams.people.blocked")}
        </Chip>
      </TooltipTrigger>
      <TooltipContent className="max-w-64 text-left">
        {since && <span className="block">{since}</span>}
        <span className="block">{t("teams.people.blockedEffect")}</span>
      </TooltipContent>
    </Tooltip>
  );
}
